import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  query, 
  where,
  Timestamp,
  arrayUnion,
  deleteDoc,
  deleteField,
  onSnapshot,
  orderBy,
  Unsubscribe
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';
import { debugLog } from '../utils/log';


// Abonnement temps réel aux événements d'un match, triés par période, minute, secondes
export const subscribeMatchEvents = (
  matchId: string,
  callback: (events: MatchEvent[]) => void
): Unsubscribe => {
  const eventsRef = collection(db, 'matches', matchId, 'events');
  const q = query(
    eventsRef,
    orderBy('period'),
    orderBy('minute'),
    orderBy('seconds')
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const events: MatchEvent[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as MatchEvent;
      data.id = docSnap.id;
      events.push(data);
    });
    callback(events);
  });
  return unsubscribe;
};

// Réinitialiser complètement un match: supprimer tous les événements, remettre les scores à 0,
// réinitialiser le statut et les temps (elapsedTime, startTime, endTime, mi-temps, etc.)
export const resetMatchData = async (matchId: string): Promise<void> => {
  try {
    console.log('[MATCH SERVICE] Réinitialisation du match', { matchId });

    const matchRef = doc(db, 'matches', matchId);

    // 1) Supprimer tous les événements de la sous-collection
    const eventsColRef = collection(db, 'matches', matchId, 'events');
    const eventsSnap = await getDocs(eventsColRef);
    const deletePromises: Promise<void>[] = [];
    eventsSnap.forEach((eventDoc) => {
      deletePromises.push(deleteDoc(doc(db, 'matches', matchId, 'events', eventDoc.id)));
    });
    await Promise.all(deletePromises);
    console.log('[MATCH SERVICE] Tous les événements ont été supprimés');

    // 2) Mettre à jour le document du match: scores à 0, statut 'upcoming', elapsedTime=0,
    //    et suppression des champs de temps (startTime, endTime, firstHalf, secondHalf, extraTime)
    await updateDoc(matchRef, {
      scoreA: 0,
      scoreB: 0,
      status: 'upcoming',
      elapsedTime: 0,
      startTime: deleteField(),
      endTime: deleteField(),
      firstHalf: deleteField(),
      secondHalf: deleteField(),
      extraTime: deleteField(),
      periodTimes: deleteField(),
      activePeriod: deleteField(),
      running: deleteField(),
      startedAt: deleteField()
    });

    console.log('[MATCH SERVICE] Match réinitialisé dans Firestore');
  } catch (error) {
    console.error('[MATCH SERVICE] Erreur lors de la réinitialisation du match:', error);
    throw error;
  }
};

// (fonctions utilitaires non utilisées supprimées)

export interface Player {
  id?: string;
  firstName: string;
  lastName: string;
  number: number;
}

export interface Team {
  id?: string;
  name: string;
  players: Player[];
  userId?: string;
}

export interface HalfData {
  startTime: Timestamp;
  endTime?: Timestamp;
}

export interface MatchEvent {
  id?: string;
  type: string;
  team: string;
  player: string;
  minute: number;
  seconds: number;
  timestamp: Timestamp;
  period: string;  // Ajout du champ period
}

// Périodes supportées: '1' (MT1), '2' (MT2), '3' (Prol.1), '4' (Prol.2), '5' (TAB)
export type PeriodKey = '1' | '2' | '3' | '4' | '5';

// État de chrono persistant dans le document du match
export interface MatchPeriodState {
  periodTimes: Record<PeriodKey, number>; // secondes accumulées par période
  activePeriod: PeriodKey;               // période sélectionnée
  running: boolean;                      // un chrono est-il en cours ?
  startedAt?: Timestamp;                 // point de départ du chrono courant
}

export interface RawMatchEvent {
  type: string;
  team: 'teamA' | 'teamB';
  player: string;
  minute: number;
  seconds: number;
  period: string;  // Ajout du champ period
}

export interface Match {
  id?: string;
  name: string;
  teamA: Team;
  teamB: Team;
  scoreA: number;
  scoreB: number;
  date: Timestamp;
  status: 'upcoming' | 'live' | 'finished' | 'halftime';  // Ajout de 'halftime'
  events?: MatchEvent[];
  startTime?: Timestamp;
  endTime?: Timestamp;
  time?: string; 
  firstHalf?: HalfData;
  secondHalf?: HalfData;
  extraTime?: HalfData;
  penalties?: any;
  userId?: string | null; // Ajout du champ userId pour associer un match à un utilisateur
}

export const DEFAULT_MATCH: Match = {
  id: '',
  name: '',
  teamA: { 
    id: '', 
    name: '', 
    players: [] 
  },
  teamB: { 
    id: '', 
    name: '', 
    players: [] 
  },
  scoreA: 0,
  scoreB: 0,
  date: Timestamp.now(),
  status: 'upcoming',
  events: [],
  time: ''
};

export const createMatch = async (match: Match): Promise<string> => {
  try {
    // Vérifier la connexion Internet
    checkNetworkConnection();
    
    // Utiliser l'email Firebase Auth comme identifiant owner (doit matcher request.auth.token.email côté rules)
    let userId: string | null = auth.currentUser?.email ?? null;
    if (!userId) {
      // Fallback: localStorage (ancien comportement)
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr) as { email?: unknown };
          userId = typeof user.email === 'string' ? user.email : null;
        } catch (error) {
          console.error('Erreur lors de la lecture des informations utilisateur:', error);
          throw new Error('AUTH_ERROR: Problème avec les informations d\'authentification. Veuillez vous reconnecter.');
        }
      }
    }

    if (!userId) {
      console.error('Aucun utilisateur connecté (email manquant)');
      throw new Error('AUTH_ERROR: Vous devez être connecté pour créer un match.');
    }

    console.log('Utilisateur créant le match:', userId);
    
    // Créer un objet de match avec des valeurs par défaut et sans undefined
    const sanitizedMatch = {
      id: match.id || '', // Garantir un ID non undefined
      name: match.name || 'Match sans nom', // Nom par défaut si undefined
      teamA: {
        id: match.teamA?.id || '', // Garantir un ID non undefined
        name: match.teamA?.name || 'Équipe A', // Nom par défaut si undefined
        players: match.teamA?.players || [] // Tableau vide si undefined
      },
      teamB: {
        id: match.teamB?.id || '', // Garantir un ID non undefined
        name: match.teamB?.name || 'Équipe B', // Nom par défaut si undefined
        players: match.teamB?.players || [] // Tableau vide si undefined
      },
      scoreA: match.scoreA || 0, // Score par défaut si undefined
      scoreB: match.scoreB || 0, // Score par défaut si undefined
      date: match.date || Timestamp.now(), // Timestamp par défaut si undefined
      status: match.status || 'upcoming', // Statut par défaut si undefined
      events: match.events || [], // Tableau vide si undefined
      userId: auth.currentUser?.email ?? userId // Associer explicitement le match à l'utilisateur actuel
    };
    
    // Ajouter le match à la collection Firestore
    const docRef = await addDoc(collection(db, 'matches'), sanitizedMatch);
    console.log('Match créé avec ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Erreur lors de la création du match:', error);
    throw error;
  }
};

export const getMatch = async (matchId: string): Promise<Match | null> => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);
    
    if (!matchDoc.exists()) {
      return null;
    }
    
    const matchData = matchDoc.data() as Match;
    matchData.id = matchDoc.id;
    
    // Vérifier les autorisations de l'utilisateur
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const userId = user.email || user.id;
        const userRole = user.role;
        
        // Filtrage strict : l'utilisateur ne voit que ses propres matchs
        // Seul l'admin peut voir tous les matchs
        const isAdmin = userRole === 'admin';
        const isOwnMatch = matchData.userId === userId;
        
        if (isAdmin || isOwnMatch) {
          // Récupérer les événements du match depuis la sous-collection
          console.log(`[MATCH SERVICE] Récupération des événements pour le match ${matchId}`);
          const events = await getMatchEvents(matchId);
          
          // Ajouter les événements au match
          matchData.events = events;
          
          console.log(`[MATCH SERVICE] ${events.length} événements récupérés pour le match ${matchId}`);
          return matchData;
        } else {
          console.error('Accès non autorisé au match:', matchId);
          return null;
        }
      } catch (error) {
        console.error('Erreur lors de la lecture des informations utilisateur:', error);
        return null;
      }
    } else {
      console.error('Utilisateur non connecté, accès refusé au match:', matchId);
      return null;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération du match:', error);
    return null;
  }
};

export const updateMatchScore = async (matchId: string, scoreA: number, scoreB: number) => {
  const matchRef = doc(db, 'matches', matchId);
  await updateDoc(matchRef, {
    scoreA,
    scoreB
  });
};

export const addMatchEvent = async (matchId: string, eventData: Omit<MatchEvent, 'id' | 'timestamp'>) => {
  try {
    console.log('[MATCH SERVICE] Adding match event', { matchId, eventData });
    
    // Récupérer le match
    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);
    
    if (!matchDoc.exists()) {
      console.error('[MATCH SERVICE] Match not found', { matchId });
      throw new Error('Match not found');
    }
    
    const match = matchDoc.data() as Match;
    console.log('[MATCH SERVICE] Match found', { match });
    
    // Créer l'événement avec un ID unique et un timestamp
    const event: MatchEvent = {
      id: uuidv4(),
      timestamp: Timestamp.now(),
      ...eventData
    };
    console.log('[MATCH SERVICE] Created event object', { event });
    
    // Ajouter l'événement à la sous-collection events du match
    const eventsCollectionRef = collection(db, 'matches', matchId, 'events');
    await addDoc(eventsCollectionRef, event);
    console.log('[MATCH SERVICE] Event added to subcollection');
    
    // Mettre à jour le score si c'est un but ou un CSC
    if (eventData.type === 'goal') {
      console.log('[MATCH SERVICE] Event is a goal, updating score');
      
      let scoreA = match.scoreA || 0;
      let scoreB = match.scoreB || 0;
      
      if (eventData.team === 'teamA') {
        scoreA += 1;
        console.log('[MATCH SERVICE] Incrementing team A score', { 
          oldScore: match.scoreA, 
          newScore: scoreA 
        });
      } else if (eventData.team === 'teamB') {
        scoreB += 1;
        console.log('[MATCH SERVICE] Incrementing team B score', { 
          oldScore: match.scoreB, 
          newScore: scoreB 
        });
      }
    } else if (eventData.type === 'csc') {
      console.log('[MATCH SERVICE] Event is a CSC (contre son camp), updating score for opposite team');
      
      let scoreA = match.scoreA || 0;
      let scoreB = match.scoreB || 0;
      
      // Pour un CSC, on ajoute un but à l'équipe ADVERSE
      if (eventData.team === 'teamA') {
        // Si l'équipe A marque contre son camp, l'équipe B marque un but
        scoreB += 1;
        console.log('[MATCH SERVICE] CSC by team A, incrementing team B score', { 
          oldScore: match.scoreB, 
          newScore: scoreB 
        });
      } else if (eventData.team === 'teamB') {
        // Si l'équipe B marque contre son camp, l'équipe A marque un but
        scoreA += 1;
        console.log('[MATCH SERVICE] CSC by team B, incrementing team A score', { 
          oldScore: match.scoreA, 
          newScore: scoreA 
        });
      }
      
      // Mettre à jour le score dans le document du match
      await updateDoc(matchRef, {
        scoreA,
        scoreB
      });
      console.log('[MATCH SERVICE] Score updated in match document', { scoreA, scoreB });
    }
    
    return event;
  } catch (error) {
    console.error('[MATCH SERVICE] Error adding match event:', error);
    throw error;
  }
};

export const getMatchEvents = async (matchId: string): Promise<MatchEvent[]> => {
  try {
    debugLog(`[MATCH SERVICE] Début de récupération des événements pour le match ${matchId}`);
    const eventsCollectionRef = collection(db, 'matches', matchId, 'events');
    const eventsSnapshot = await getDocs(eventsCollectionRef);
    
    let events: MatchEvent[] = [];
    eventsSnapshot.forEach(doc => {
      const eventData = doc.data() as MatchEvent;
      eventData.id = doc.id;
      events.push(eventData);
    });
    
    // Créer une copie du tableau avant de le trier pour éviter l'erreur "Cannot assign to read only property"
    const sortedEvents = [...events].sort((a, b) => {
      // D'abord par période
      const periodA = parseInt(a.period || '1');
      const periodB = parseInt(b.period || '1');
      if (periodA !== periodB) return periodA - periodB;
      
      // Ensuite par minute
      if (a.minute !== b.minute) return a.minute - b.minute;
      
      // Enfin par secondes
      return a.seconds - b.seconds;
    });
    
    // Remplacer le tableau original par le tableau trié
    events = sortedEvents;
    
    debugLog(`[MATCH SERVICE] ${events.length} événements récupérés et triés pour le match ${matchId}`);
    return events;
  } catch (error) {
    console.error('Erreur lors de la récupération des événements du match:', error);
    return [];
  }
};

export const createTeam = async (teamData: Omit<Team, 'id'>): Promise<string> => {
  const email = auth.currentUser?.email;
  if (!email) {
    throw new Error('Utilisateur non authentifié (email manquant)');
  }

  const docRef = await addDoc(collection(db, 'teams'), {
    ...teamData,
    userId: email,
  });
  return docRef.id;
};

export const addPlayerToTeam = async (teamId: string, player: Player) => {
  const teamRef = doc(db, 'teams', teamId);
  await updateDoc(teamRef, {
    players: arrayUnion(player)
  });
};

export const startMatch = async (matchId: string) => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    const matchSnapshot = await getDoc(matchRef);
    const matchData = matchSnapshot.data();
    const now = Timestamp.now();
    
    // Vérifier si le match a déjà été démarré puis arrêté
    // Si oui, nous voulons conserver le temps écoulé précédemment
    if (matchData && matchData.elapsedTime !== undefined) {
      console.log(`Match repris avec ${matchData.elapsedTime} secondes déjà écoulées`);
      
      await updateDoc(matchRef, {
        status: 'live',
        startTime: now,
        // Ne pas réinitialiser elapsedTime pour permettre la reprise du temps
      });
    } else {
      // Premier démarrage du match
      await updateDoc(matchRef, {
        status: 'live',
        startTime: now,
        elapsedTime: 0, // Initialiser le temps écoulé à 0
        firstHalf: {
          startTime: now
        }
      });
    }
    
    console.log('Match démarré:', matchId);
    return matchData?.elapsedTime || 0; // Retourner le temps écoulé
  } catch (error) {
    console.error('Erreur lors du démarrage du match:', error);
    throw error;
  }
};

// --- Gestion du chrono par période (persistance Firestore) ---

// Garantit une structure par défaut pour l'état de périodes
const defaultPeriodState = (): MatchPeriodState => ({
  periodTimes: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
  activePeriod: '1',
  running: false,
  startedAt: undefined
});

// Abonnement aux champs de chrono/périodes du document match
export const subscribeMatchPeriodState = (
  matchId: string,
  callback: (state: MatchPeriodState) => void
): Unsubscribe => {
  const matchRef = doc(db, 'matches', matchId);
  const unsubscribe = onSnapshot(matchRef, (snap) => {
    const data = snap.data() || {};
    const state: MatchPeriodState = {
      ...defaultPeriodState(),
      ...('periodTimes' in data ? { periodTimes: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, ...data.periodTimes } } : {}),
      ...(data.activePeriod ? { activePeriod: data.activePeriod as PeriodKey } : {}),
      ...(typeof data.running === 'boolean' ? { running: data.running } : {}),
      ...(data.startedAt ? { startedAt: data.startedAt as Timestamp } : {})
    } as MatchPeriodState;
    callback(state);
  });
  return unsubscribe;
};

// Définir la période active sans démarrer le chrono
export const setActivePeriod = async (matchId: string, period: PeriodKey): Promise<void> => {
  const matchRef = doc(db, 'matches', matchId);
  await updateDoc(matchRef, {
    activePeriod: period
  });
};

// Démarrer (ou redémarrer) la période active: running=true et startedAt=now
export const startCurrentPeriod = async (matchId: string, period: PeriodKey): Promise<void> => {
  const matchRef = doc(db, 'matches', matchId);
  await updateDoc(matchRef, {
    activePeriod: period,
    running: true,
    startedAt: Timestamp.now()
  });
};

// Mettre en pause la période courante: accumuler le temps et arrêter le chrono
export const pauseCurrentPeriod = async (matchId: string): Promise<void> => {
  const matchRef = doc(db, 'matches', matchId);
  const snap = await getDoc(matchRef);
  if (!snap.exists()) return;
  const data = snap.data() as any;
  const state = defaultPeriodState();
  if (data.periodTimes) state.periodTimes = { ...state.periodTimes, ...data.periodTimes };
  if (data.activePeriod) state.activePeriod = data.activePeriod as PeriodKey;
  if (typeof data.running === 'boolean') state.running = data.running;
  if (data.startedAt) state.startedAt = data.startedAt as Timestamp;

  if (!state.running || !state.startedAt) {
    // Rien à faire si déjà en pause
    await updateDoc(matchRef, { running: false, startedAt: deleteField() });
    return;
  }

  const now = Timestamp.now();
  const elapsed = Math.max(0, Math.floor((now.toDate().getTime() - state.startedAt.toDate().getTime()) / 1000));
  const current = state.periodTimes[state.activePeriod] || 0;
  const newTimes = { ...state.periodTimes, [state.activePeriod]: current + elapsed } as Record<PeriodKey, number>;

  await updateDoc(matchRef, {
    periodTimes: newTimes,
    running: false,
    startedAt: deleteField()
  });
};

// Basculer de période en garantissant aucune fuite de chrono en cours
export const switchActivePeriod = async (matchId: string, newPeriod: PeriodKey): Promise<void> => {
  // Pause d'abord pour accumuler si nécessaire
  await pauseCurrentPeriod(matchId);
  // Puis changer la période active sans démarrer
  await setActivePeriod(matchId, newPeriod);
};

export const endMatch = async (matchId: string, elapsedTime: number = 0) => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    const now = Timestamp.now();
    
    await updateDoc(matchRef, {
      status: 'finished',
      endTime: now,
      elapsedTime: elapsedTime // Sauvegarder le temps écoulé
    });
    
    console.log('Match terminé:', matchId, 'Temps écoulé:', elapsedTime, 'secondes');
  } catch (error) {
    console.error('Erreur lors de la fin du match:', error);
    throw error;
  }
};

export const safeConvertToTimestamp = (date: Date | string | Timestamp | any): Timestamp => {
  if (!date) {
    return Timestamp.now();
  }
  
  if (date instanceof Timestamp) {
    return date;
  }
  
  if (date instanceof Date) {
    return Timestamp.fromDate(date);
  }
  
  if (typeof date === 'string') {
    try {
      return Timestamp.fromDate(new Date(date));
    } catch (error) {
      console.error('Erreur lors de la conversion de la chaîne en date:', error);
      return Timestamp.now();
    }
  }
  
  // Si le format n'est pas reconnu, retourner la date actuelle
  console.warn('Format de date non reconnu, utilisation de la date actuelle');
  return Timestamp.now();
};

export const getTeam = async (teamId: string): Promise<Team | null> => {
  try {
    // Vérifier que l'ID de l'équipe est valide
    if (!teamId) {
      console.error('ID de l\'équipe non fourni');
      return null;
    }

    const teamRef = doc(db, 'teams', teamId);
    const teamDoc = await getDoc(teamRef);
    
    if (!teamDoc.exists()) {
      console.error('Équipe non trouvée:', teamId);
      return null;
    }
    
    const teamData = teamDoc.data() as Team;
    
    return {
      id: teamDoc.id,
      name: teamData.name,
      players: teamData.players || []
    };
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'équipe:', error);
    return null;
  }
};

// Vérifier l'état de la connexion Internet
const checkNetworkConnection = () => {
  if (!navigator.onLine) {
    throw new Error('NETWORK_ERROR: Vous êtes actuellement hors ligne. Veuillez vous connecter à Internet pour continuer.');
  }
};

// Fonction principale pour récupérer tous les matchs
export const getAllMatches = async (): Promise<Match[]> => {
  try {
    // Vérifier la connexion Internet
    checkNetworkConnection();
    
    // Vérifier si l'utilisateur est authentifié
    const userStr = localStorage.getItem('user');
    let userId = null;
    let userRole = null;
    
    const isDev = process.env.NODE_ENV !== 'production';

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userId = user.email || user.id; // Utiliser l'email ou l'id comme identifiant unique
        userRole = user.role; // Récupérer le rôle de l'utilisateur
        if (isDev) {
          console.log('Utilisateur authentifié:', { userId, userRole });
        }
      } catch (error) {
        console.error('Erreur lors de la lecture des informations utilisateur:', error);
        throw new Error('AUTH_ERROR: Problème avec les informations d\'authentification. Veuillez vous reconnecter.');
      }
    } else {
      if (isDev) {
        console.log('Aucun utilisateur connecté');
      }
      throw new Error('AUTH_ERROR: Aucun utilisateur connecté. Veuillez vous authentifier pour accéder aux matchs.');
    }
    
    // Récupérer les matchs (requête filtrée pour les non-admin)
    const matchesCollectionRef = collection(db, 'matches');
    const isAdmin = userRole === 'admin';
    const matchesQuery = isAdmin ? query(matchesCollectionRef) : query(matchesCollectionRef, where('userId', '==', userId));
    const matchesSnapshot = await getDocs(matchesQuery);
    const matches: Match[] = [];
    
    for (const matchDoc of matchesSnapshot.docs) {
      const matchData = matchDoc.data();
      const matchUserId = matchData.userId || null;
      
      // Garde-fou : même si une requête non filtrée passait, on ne retourne que les matchs autorisés
      const isOwnMatch = matchUserId === userId;
      if (isAdmin || isOwnMatch) {
        const match: Match = {
          id: matchDoc.id,
          name: matchData.name || '',
          teamA: matchData.teamA || { id: '', name: '', players: [] },
          teamB: matchData.teamB || { id: '', name: '', players: [] },
          scoreA: matchData.scoreA || 0,
          scoreB: matchData.scoreB || 0,
          date: matchData.date || Timestamp.now(),
          status: matchData.status || 'upcoming',
          events: [],
          time: matchData.time || '',
          userId: matchUserId
        };
        
        // Charger les événements du match
        try {
          const eventsCollection = collection(db, 'matches', matchDoc.id, 'events');
          const eventsSnapshot = await getDocs(eventsCollection);
          const events: MatchEvent[] = [];
          
          eventsSnapshot.docs.forEach(eventDoc => {
            const eventData = eventDoc.data();
            events.push({
              id: eventDoc.id,
              type: eventData.type,
              team: eventData.team,
              player: eventData.player,
              minute: eventData.minute,
              seconds: eventData.seconds,
              timestamp: eventData.timestamp,
              period: eventData.period || '1'
            });
          });
          
          match.events = events;
        } catch (error) {
          console.error('Erreur lors du chargement des événements:', error);
        }
        
        matches.push(match);
      }
    }
    
    if (isDev) {
      console.log('Matches chargés avec événements:', matches);
    }
    return matches;
  } catch (error) {
    console.error('Erreur lors du chargement des matchs:', error);
    
    // Gestion spécifique des erreurs réseau et d'authentification
    if (error instanceof Error) {
      if (error.message.startsWith('NETWORK_ERROR:')) {
        // Erreur de connexion réseau
        throw error;
      } else if (error.message.startsWith('AUTH_ERROR:')) {
        // Erreur d'authentification
        throw error;
      } else if (!navigator.onLine) {
        // Détection supplémentaire de problème de connexion
        throw new Error('NETWORK_ERROR: Connexion Internet perdue. Veuillez vérifier votre connexion et réessayer.');
      }
    }
    
    // Erreur générique Firebase/Firestore
    throw new Error('SERVICE_ERROR: Problème lors de la récupération des matchs. Veuillez réessayer ultérieurement.');
  }
};

export const deleteMatchEvent = async (matchId: string, eventIndex: number): Promise<void> => {
  try {
    console.log('[MATCH SERVICE] Deleting match event', { matchId, eventIndex });
    
    // 1. Récupérer tous les événements de la sous-collection
    const events = await getMatchEvents(matchId);
    
    // 2. Vérifier si des événements existent
    if (!events || events.length === 0) {
      console.error('[MATCH SERVICE] No events found for this match', { matchId });
      throw new Error('No events found for this match');
    }
    
    // 3. Vérifier si l'index est valide
    if (eventIndex < 0 || eventIndex >= events.length) {
      console.error('[MATCH SERVICE] Invalid event index', { matchId, eventIndex, totalEvents: events.length });
      throw new Error(`Invalid event index: ${eventIndex}. Total events: ${events.length}`);
    }

    // 4. Obtenir l'événement à supprimer
    const eventToDelete = events[eventIndex];
    console.log('[MATCH SERVICE] Found event to delete', { eventToDelete });
    
    if (!eventToDelete.id) {
      console.error('[MATCH SERVICE] Event ID not found', { eventToDelete });
      throw new Error('Event ID not found');
    }

    // 5. Supprimer l'événement de la sous-collection
    const eventRef = doc(db, 'matches', matchId, 'events', eventToDelete.id);
    await deleteDoc(eventRef);
    
    // 6. Récupérer le document du match pour mettre à jour le score si nécessaire
    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);
    
    if (!matchDoc.exists()) {
      console.error('[MATCH SERVICE] Match not found', { matchId });
      throw new Error('Match not found');
    }

    const match = matchDoc.data() as Match;
    
    // 7. Mettre à jour le score si c'était un but
    if (eventToDelete.type === 'goal') {
      console.log('[MATCH SERVICE] Updating score for deleted goal', {
        team: eventToDelete.team,
        currentScoreA: match.scoreA,
        currentScoreB: match.scoreB
      });

      const updates: any = {};
      
      if (eventToDelete.team === 'teamA') {
        updates.scoreA = Math.max(0, match.scoreA - 1);
        console.log('[MATCH SERVICE] Updating team A score', { 
          oldScore: match.scoreA, 
          newScore: updates.scoreA 
        });
      } else if (eventToDelete.team === 'teamB') {
        updates.scoreB = Math.max(0, match.scoreB - 1);
        console.log('[MATCH SERVICE] Updating team B score', { 
          oldScore: match.scoreB, 
          newScore: updates.scoreB 
        });
      }
      
      // Mettre à jour le score dans le document du match
      if (Object.keys(updates).length > 0) {
        await updateDoc(matchRef, updates);
      }
    }

    console.log('[MATCH SERVICE] Event deleted successfully', { 
      matchId, 
      eventIndex,
      eventId: eventToDelete.id
    });

    return;
  } catch (error) {
    console.error('[MATCH SERVICE] Error deleting match event:', error);
    throw error;
  }
};

// Fonction pour mettre à jour un match existant
export const updateMatch = async (matchId: string, matchData: Partial<Match>): Promise<void> => {
  try {
    // Vérifier la connexion Internet
    checkNetworkConnection();
    
    // Vérifier si l'utilisateur est authentifié
    const userStr = localStorage.getItem('user');
    let userId = null;
    let userRole = null;
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userId = user.email; // Utiliser l'email comme identifiant unique
        userRole = user.role; // Récupérer le rôle de l'utilisateur
      } catch (error) {
        console.error('Erreur lors de la lecture des informations utilisateur:', error);
        throw new Error('AUTH_ERROR: Problème avec les informations d\'authentification. Veuillez vous reconnecter.');
      }
    } else {
      throw new Error('AUTH_ERROR: Aucun utilisateur connecté. Veuillez vous authentifier pour modifier un match.');
    }
    
    // Récupérer le match existant
    const matchRef = doc(db, 'matches', matchId);
    const matchSnap = await getDoc(matchRef);
    
    if (!matchSnap.exists()) {
      throw new Error(`Match avec l'ID ${matchId} non trouvé`);
    }
    
    const existingMatch = matchSnap.data() as Match;
    
    // Vérifier que l'utilisateur est propriétaire du match ou admin
    const isAdmin = userRole === 'admin';
    const isOwner = existingMatch.userId === userId;
    
    if (!isAdmin && !isOwner) {
      throw new Error('Vous n\'avez pas les droits pour modifier ce match');
    }
    
    // Préparer les données à mettre à jour
    const updates: Record<string, any> = {};
    
    // Mettre à jour uniquement les champs fournis
    if (matchData.name !== undefined) updates.name = matchData.name;
    if (matchData.date !== undefined) updates.date = matchData.date;
    if (matchData.status !== undefined) updates.status = matchData.status;
    if (matchData.time !== undefined) updates.time = matchData.time;
    
    // Mise à jour des équipes
    if (matchData.teamA) {
      if (matchData.teamA.name !== undefined) updates['teamA.name'] = matchData.teamA.name;
      if (matchData.teamA.players !== undefined) updates['teamA.players'] = matchData.teamA.players;
    }
    
    if (matchData.teamB) {
      if (matchData.teamB.name !== undefined) updates['teamB.name'] = matchData.teamB.name;
      if (matchData.teamB.players !== undefined) updates['teamB.players'] = matchData.teamB.players;
    }
    
    // Mise à jour des scores
    if (matchData.scoreA !== undefined) updates.scoreA = matchData.scoreA;
    if (matchData.scoreB !== undefined) updates.scoreB = matchData.scoreB;
    
    // Mettre à jour le document dans Firestore
    await updateDoc(matchRef, updates);
    
    console.log(`Match ${matchId} mis à jour avec succès`, updates);
  } catch (error) {
    console.error('Erreur lors de la mise à jour du match:', error);
    
    // Gestion spécifique des erreurs réseau et d'authentification
    if (error instanceof Error) {
      if (error.message.startsWith('NETWORK_ERROR:') || 
          error.message.startsWith('AUTH_ERROR:')) {
        throw error;
      } else if (!navigator.onLine) {
        throw new Error('NETWORK_ERROR: Connexion Internet perdue. Veuillez vérifier votre connexion et réessayer.');
      }
    }
    
    throw new Error('SERVICE_ERROR: Problème lors de la mise à jour du match. Veuillez réessayer ultérieurement.');
  }
};

// Alias pour compatibilité
export const saveMatch = createMatch;
export const saveTeam = createTeam;
export const getMatches = getAllMatches; // Alias de getAllMatches

// Réexportation explicite de deleteMatch pour éviter les erreurs
// La fonction est déjà définie plus haut dans le fichier

// Type pour représenter un match avec toutes les propriétés Timestamp converties en chaînes
export type SerializableMatch = Omit<Match, 'date' | 'startTime' | 'endTime' | 'events' | 'firstHalf' | 'secondHalf' | 'extraTime'> & {
  date: string;
  startTime?: string;
  endTime?: string;
  events?: Array<Omit<MatchEvent, 'timestamp'> & { timestamp: string }>;
  firstHalf?: Omit<HalfData, 'startTime' | 'endTime'> & { startTime: string; endTime?: string };
  secondHalf?: Omit<HalfData, 'startTime' | 'endTime'> & { startTime: string; endTime?: string };
  extraTime?: Omit<HalfData, 'startTime' | 'endTime'> & { startTime: string; endTime?: string };
};

export const convertToSerializableMatch = (match: Match = DEFAULT_MATCH): SerializableMatch => {
  debugLog('[SERIALIZATION] Début de la conversion du match en format sérialisable', { 
    matchId: match?.id || 'undefined', 
    dateType: match?.date ? (match.date instanceof Timestamp ? 'Timestamp' : typeof match.date) : 'undefined',
    hasEvents: !!match?.events,
    eventsCount: match?.events?.length || 0
  });
  
  if (!match) return convertToSerializableMatch(DEFAULT_MATCH);
  
  // Fonction utilitaire pour convertir tous les Timestamps en chaînes
  const convertTimestamps = (obj: any): any => {
    if (!obj) return obj;
    
    // Si c'est un Timestamp, le convertir en chaîne
    if (obj instanceof Timestamp) {
      return obj.toDate().toISOString();
    }
    
    // Si c'est un tableau, convertir chaque élément
    if (Array.isArray(obj)) {
      return obj.map(item => convertTimestamps(item));
    }
    
    // Si c'est un objet, convertir récursivement toutes ses propriétés
    if (typeof obj === 'object' && obj !== null) {
      const result: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = convertTimestamps(obj[key]);
        }
      }
      return result;
    }
    
    // Sinon, retourner l'objet tel quel
    return obj;
  };
  
  // Créer une copie du match pour éviter de modifier l'original
  const matchCopy: any = { ...match };
  
  // Convertir explicitement les champs Timestamp connus
  if (matchCopy.date instanceof Timestamp) {
    matchCopy.date = matchCopy.date.toDate().toISOString();
  }
  
  if (matchCopy.startTime instanceof Timestamp) {
    matchCopy.startTime = matchCopy.startTime.toDate().toISOString();
  }
  
  if (matchCopy.endTime instanceof Timestamp) {
    matchCopy.endTime = matchCopy.endTime.toDate().toISOString();
  }
  
  // Convertir les événements du match
  if (matchCopy.events && Array.isArray(matchCopy.events)) {
    debugLog('[SERIALIZATION] Conversion des événements', { 
      eventsCount: matchCopy.events.length,
      firstEventType: matchCopy.events[0]?.type || 'aucun',
      firstEventTimestampType: matchCopy.events[0]?.timestamp ? 
        (matchCopy.events[0].timestamp instanceof Timestamp ? 'Timestamp' : typeof matchCopy.events[0].timestamp) : 'undefined'
    });
    
    matchCopy.events = matchCopy.events.map((event: MatchEvent, index: number) => {
      debugLog(`[SERIALIZATION] Conversion de l'événement #${index}`, { 
        eventType: event.type, 
        timestampType: event.timestamp ? 
          (event.timestamp instanceof Timestamp ? 'Timestamp' : typeof event.timestamp) : 'undefined'
      });
      
      const eventCopy: Omit<MatchEvent, 'timestamp'> & { timestamp: string } = { 
        ...event,
        timestamp: event.timestamp instanceof Timestamp 
          ? event.timestamp.toDate().toISOString() 
          : typeof event.timestamp === 'string' 
            ? event.timestamp 
            : new Date().toISOString()
      };
      return eventCopy;
    });
  }
  
  // Convertir les données des mi-temps
  if (matchCopy.firstHalf) {
    const firstHalf = { ...matchCopy.firstHalf };
    if (firstHalf.startTime instanceof Timestamp) {
      firstHalf.startTime = firstHalf.startTime.toDate().toISOString();
    }
    if (firstHalf.endTime instanceof Timestamp) {
      firstHalf.endTime = firstHalf.endTime.toDate().toISOString();
    }
    matchCopy.firstHalf = firstHalf;
  }
  
  if (matchCopy.secondHalf) {
    const secondHalf = { ...matchCopy.secondHalf };
    if (secondHalf.startTime instanceof Timestamp) {
      secondHalf.startTime = secondHalf.startTime.toDate().toISOString();
    }
    if (secondHalf.endTime instanceof Timestamp) {
      secondHalf.endTime = secondHalf.endTime.toDate().toISOString();
    }
    matchCopy.secondHalf = secondHalf;
  }
  
  if (matchCopy.extraTime) {
    const extraTime = { ...matchCopy.extraTime };
    if (extraTime.startTime instanceof Timestamp) {
      extraTime.startTime = extraTime.startTime.toDate().toISOString();
    }
    if (extraTime.endTime instanceof Timestamp) {
      extraTime.endTime = extraTime.endTime.toDate().toISOString();
    }
    matchCopy.extraTime = extraTime;
  }
  
  // Convertir récursivement tous les autres Timestamps qui pourraient être présents
  const result = convertTimestamps(matchCopy) as SerializableMatch;
  
  debugLog('[SERIALIZATION] Match converti avec succès', { 
    matchId: result.id,
    dateType: typeof result.date,
    eventsCount: result.events?.length || 0,
    halfsInfo: {
      firstHalf: result.firstHalf ? 'présent' : 'absent',
      secondHalf: result.secondHalf ? 'présent' : 'absent',
      extraTime: result.extraTime ? 'présent' : 'absent'
    }
  });
  
  return result;
};

export const prepareMatchEvent = (rawEvent: RawMatchEvent): MatchEvent => ({
  id: uuidv4(),
  type: rawEvent.type,
  team: rawEvent.team,
  player: rawEvent.player,
  minute: rawEvent.minute,
  seconds: rawEvent.seconds,
  timestamp: Timestamp.now(),
  period: rawEvent.period
});

export const validateEventType = (type: string): string => {
  const validTypes = [
    'goal', 'jaune', 'rouge', 'remplacement', 
    'occasion', 'blesse', 'passe_decisive', 'tir_cadre'
  ];

  return validTypes.includes(type) ? type : 'goal';
};

